import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import PermissionResourceSchema from "permissions/resources/_schemas/permission-resource-schema";
import { AccessLevel, ResourceType, Scope } from "permissions/resources/permission-resource-types";
import { z } from "zod";

export type CommunicationsAccessJSON = z.infer<typeof CommunicationsAccessSchema.CommunicationsAccessJSONZ>;

export type CommunicationsAccessParams = z.infer<typeof CommunicationsAccessSchema.CommunicationsAccessZ>;

export default class CommunicationsAccessSchema {
  public static readonly CommunicationsAccessJSONZ = z.object({
    ...PermissionResourceSchema.ResourceJSONZ.shape,
    resourceType: z.literal(ResourceType.CommunicationsAccess),
    accessLevel: z.enum(AccessLevel).nullish(),
    scope: z.enum(Scope).nullish(),
  });

  public static readonly CommunicationsAccessZ = z.object({
    ...PermissionResourceSchema.ResourceZ.shape,
    resourceType: z.literal(ResourceType.CommunicationsAccess),
    accessLevel: z.enum(AccessLevel).optional(),
    scope: z.enum(Scope).optional(),
  });

  public static validate(values: unknown): CommunicationsAccessParams {
    return parseOrThrow(CommunicationsAccessSchema.CommunicationsAccessZ, values, {
      logError: true,
      schemaName: "CommunicationsAccess",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(values: unknown): CommunicationsAccessJSON {
    return parseOrThrow(CommunicationsAccessSchema.CommunicationsAccessJSONZ, values, {
      logError: true,
      schemaName: "CommunicationsAccess",
      operation: "validateJSON",
      payloadSample: values,
    });
  }

  public static isValid(values: unknown): values is CommunicationsAccessParams {
    try {
      CommunicationsAccessSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(values: unknown): values is CommunicationsAccessJSON {
    try {
      CommunicationsAccessSchema.validateJSON(values);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(data: CommunicationsAccessJSON): CommunicationsAccessParams {
    const parsed = parseOrThrow(CommunicationsAccessSchema.CommunicationsAccessJSONZ, data, {
      logError: true,
      schemaName: "CommunicationsAccess",
      operation: "deserialize",
      payloadSample: data,
    });
    return CommunicationsAccessSchema.codec.decode(parsed);
  }

  public static serialize(notification: CommunicationsAccessParams): CommunicationsAccessJSON {
    const parsed = CommunicationsAccessSchema.validate(notification);
    return CommunicationsAccessSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(
    CommunicationsAccessSchema.CommunicationsAccessJSONZ,
    CommunicationsAccessSchema.CommunicationsAccessZ,
    {
      decode: (data) => ({
        ...PermissionResourceSchema.deserialize(data),
        resourceType: ResourceType.CommunicationsAccess as const,
        accessLevel: data.accessLevel ?? undefined,
        scope: data.scope ?? undefined,
      }),
      encode: (resource) => ({
        ...PermissionResourceSchema.serialize(resource),
        resourceType: ResourceType.CommunicationsAccess as const,
        accessLevel: resource.accessLevel,
        scope: resource.scope,
      }),
    }
  );
}
